import { formatMessagesWithAmount } from "@/lib/translations/translations";
import { useLocalizationStore } from "@/stores/localization-store/localization-store";
import type { LocalizationStore } from "@/stores/localization-store/localization-store.types";

type TranslationOptions = {
  titleCase?: boolean;
  lowerCase?: boolean;
  upperCase?: boolean;
};

type GetValueReturnType =
  | string
  | number
  | object
  | unknown[]
  | null
  | Record<string, unknown>;

export function useTranslations() {
  const translations = useLocalizationStore(
    (state: LocalizationStore) => state.translations
  );
  const fallbackTranslations = useLocalizationStore(
    (state: LocalizationStore) => state.fallbackTranslations
  );

  /**
   * Returns translation
   * @param key
   * @param replacements
   * @param options
   */
  const __ = (
    key: string,
    replacements: Record<string, string> = {},
    options: TranslationOptions = {
      titleCase: false,
      lowerCase: false,
      upperCase: false,
    }
  ) => {
    let translatedValue = getValue(key, replacements);

    if (options.titleCase) {
      translatedValue = translatedValue.replace(/(^|\s)\S/g, (a) =>
        a.toUpperCase()
      );
    }

    if (options.lowerCase) {
      translatedValue = translatedValue.toLowerCase();
    }

    if (options.upperCase) {
      translatedValue = translatedValue.toUpperCase();
    }

    return translatedValue;
  };

  /**
   * Returns translation based on amount (`{1} user|[2,*] users` => `user` or `users`)
   * @param key
   * @param amount
   * @param replacements
   */
  const __x = (
    key: string,
    amount: number,
    replacements: Record<string, string> = {}
  ) => getTranslationByAmount(key, amount, replacements);

  /**
   * Returns translation based on amount along with the amount (`1 user`, `14 conversations`)
   * @param key
   * @param amount
   * @param replacements
   */
  const __xw = (
    key: string,
    amount: number,
    replacements: Record<string, string> = {}
  ) => {
    const translation = __x(key, amount, replacements);

    return `${amount} ${translation}`;
  };

  /**
   * Returns the raw value of the specified translations (other methods cast to string).
   * Allows for usage of iterables.
   *
   * @param key
   */
  const __raw = (key: string): GetValueReturnType => getValueWithFallback(key);

  const getTranslationByAmount = (
    key: string,
    amount: number,
    replacements: Record<string, string> = {}
  ) => {
    const translation = __(key, replacements);

    const { messages, foundMessage } = formatMessagesWithAmount(
      translation,
      amount
    );

    if (foundMessage) {
      return foundMessage;
    }

    // Skip amount check if there's only one message
    if (messages.length === 1) {
      return translation;
    }

    const usePlural = amount !== 1;
    const indexToUse = usePlural ? 1 : 0;

    return messages[indexToUse];
  };

  const getValue = (
    key: string,
    replacements: Record<string, string> = {}
  ): string => {
    const translation = getValueWithFallback(key)?.toString() || "";

    return applyReplacements(translation, replacements);
  };

  const getValueWithFallback = (key: string): GetValueReturnType =>
    getNestedValue(translations, key) ||
    getNestedValue(fallbackTranslations, key) ||
    null;

  const getNestedValue = (data: unknown, path: string): string | unknown =>
    path.split(".").reduce((a, b) => (a && b ? a[b] : null), data);

  const escapeRegex = (str: string): string =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const applyReplacements = (
    translation: string,
    replacements: Record<string, string>
  ): string => {
    let replaced = translation;

    for (const replacement of Object.keys(replacements)) {
      const pattern = new RegExp(`:${escapeRegex(replacement)}(?!\\w)`, "g");
      replaced = replaced.replace(pattern, replacements[replacement]);
    }

    return replaced;
  };

  return {
    __,
    __x,
    __xw,
    __raw,
  };
}
